/**
 * Ecstatic Oracle Web Preview
 * Main entry point for the session preview web app
 *
 * URL: play.ecstaticoracle.dance/{shareId}
 */

import './style.css';
import {
  getSharedSession,
  SharedSession,
  getUserProfile,
  ProfileResponse,
  listPublicProfiles,
  listPublicSessions,
  listPublicEvents,
  DiscoveryProfile,
  DiscoverySession,
  formatDuration,
} from './api/firebase';
import { renderSessionHeader } from './components/SessionHeader';
import { renderDeviationGraph } from './components/DeviationGraph';
import { renderStatistics } from './components/Statistics';
import { renderWaveformPlayer } from './components/WaveformPlayer';
import { renderDownloadCTA, renderFooter } from './components/DownloadCTA';
import { renderProfileHeader, renderProfileStats, renderDJProfile, renderProfileSessions } from './components/UserProfile';
import { renderNavigation, initNavigationListeners } from './components/Navigation';
import { renderBreadcrumbs } from './components/Breadcrumbs';
import { initEventPage } from './event-page';
import { initEventsPage } from './events-page';
import { setPageMeta, setJsonLd, setCanonical } from './utils/seo';

/**
 * Route types
 */
type RouteType = 'landing' | 'discover' | 'discover-map' | 'session' | 'profile' | 'event' | 'events';

interface Route {
  type: RouteType;
  id?: string;
  filters?: {
    category?: string;
    location?: string;
  };
}

/**
 * Pagination state for discover page
 */
let discoverPaginationState = {
  sessions: [] as DiscoverySession[],
  lastCursor: null as string | null,
  hasMore: true,
  isLoading: false,
  category: null as string | null,
  searchQuery: '',
};

/**
 * Initialize the app
 */
async function init(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;

  // Determine route type
  const route = getRouteFromUrl();

  // Inject global navigation on all pages
  app.insertAdjacentHTML('beforebegin', renderNavigation({ currentPath: window.location.pathname }));
  initNavigationListeners();

  switch (route.type) {
    case 'event':
      await initEventPage(app, route.id!);
      break;
    case 'events':
      await initEventsPage(app, route.filters as { status?: string } | undefined);
      break;
    case 'profile':
      await initProfilePage(app, route.id!);
      break;
    case 'session':
      await initSessionPage(app, route.id!);
      break;
    case 'discover':
      await initDiscoverPage(app, route.filters);
      break;
    case 'discover-map':
      await initMapPage(app);
      break;
    default:
      await initLandingPage(app);
  }
}

/**
 * Initialize session preview page
 */
async function initSessionPage(app: HTMLElement, shareId: string): Promise<void> {
  try {
    // Fetch session data
    const session = await getSharedSession(shareId);
    renderSession(app, session);

    // SEO
    const totalBits = session.statistics.totalBits || 0;
    setPageMeta({
      title: `${session.title} - Ecstatic Oracle`,
      description: `Z-Score: ${session.statistics.zScore.toFixed(3)} | ${totalBits.toLocaleString()} bits analyzed`,
    });
    setCanonical(`https://play.ecstaticoracle.dance/${shareId}`);
    setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      'name': session.title,
      'dateCreated': session.createdAt,
      'url': `https://play.ecstaticoracle.dance/${shareId}`,
    });

    // Inject breadcrumbs
    const breadcrumbHtml = renderBreadcrumbs([
      { label: 'Home', href: '/' },
      { label: 'Sessions', href: '/discover' },
      { label: session.title },
    ]);
    app.querySelector('.session-preview')?.insertAdjacentHTML('afterbegin', breadcrumbHtml);

  } catch (error) {
    console.error('Failed to load session:', error);
    renderError(app, error instanceof Error ? error.message : 'Failed to load session', 'Session Not Found');
  }
}

/**
 * Initialize user profile page
 */
async function initProfilePage(app: HTMLElement, userId: string): Promise<void> {
  try {
    // Show loading state
    app.innerHTML = `
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    `;

    // Fetch profile data
    const data = await getUserProfile(userId);
    renderProfile(app, data);

    // SEO
    const title = data.profile.isDJ && data.profile.djProfile?.stageName
      ? `${data.profile.displayName} (${data.profile.djProfile.stageName}) - Ecstatic Oracle`
      : `${data.profile.displayName} - Ecstatic Oracle`;
    const description = data.profile.bio
      ? data.profile.bio.substring(0, 150)
      : `${data.profile.sessionCount} sessions | ${data.profile.totalBitsGenerated.toLocaleString()} bits generated`;
    setPageMeta({ title, description });
    setCanonical(`https://play.ecstaticoracle.dance/user/${userId}`);
    setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'Person',
      'name': data.profile.displayName,
      'url': `https://play.ecstaticoracle.dance/user/${userId}`,
    });

    // Inject breadcrumbs
    const breadcrumbHtml = renderBreadcrumbs([
      { label: 'Home', href: '/' },
      { label: data.profile.displayName },
    ]);
    app.querySelector('.profile-preview')?.insertAdjacentHTML('afterbegin', breadcrumbHtml);

  } catch (error) {
    console.error('Failed to load profile:', error);
    renderError(app, error instanceof Error ? error.message : 'Failed to load profile', 'Profile Not Found');
  }
}

/**
 * Determine the route from URL
 * Supports:
 * - /discover - Discover page with sessions and profiles
 * - /user/{userId} - User profile page
 * - /{shareId} - Session preview page (short 6-char ID or UUID)
 * - / - Landing page
 */
function getRouteFromUrl(): Route {
  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);
  const hashPath = window.location.hash.replace('#/', '');

  // Check for discover map page: /discover/map
  if (pathname === '/discover/map' || pathname === '/discover/map/') {
    return { type: 'discover-map' };
  }

  // Check for discover page: /discover
  if (pathname === '/discover' || pathname === '/discover/') {
    return {
      type: 'discover',
      filters: {
        category: searchParams.get('category') || undefined,
        location: searchParams.get('location') || undefined,
      },
    };
  }

  // Check for events discovery page: /events
  if (pathname === '/events' || pathname === '/events/') {
    return {
      type: 'events',
      filters: {
        category: searchParams.get('status') || undefined,
      },
    };
  }

  // Check for event page: /event/{joinCode}
  const eventMatch = pathname.match(/^\/event\/([A-Z0-9]{6})$/i);
  if (eventMatch) {
    return { type: 'event', id: eventMatch[1].toUpperCase() };
  }

  // Check for user profile route: /user/{userId}
  const userMatch = pathname.match(/^\/user\/([a-zA-Z0-9]{20,40})$/);
  if (userMatch) {
    return { type: 'profile', id: userMatch[1] };
  }

  // Check for query param user: ?user={userId}
  const userParam = searchParams.get('user');
  if (userParam && /^[a-zA-Z0-9]{20,40}$/.test(userParam)) {
    return { type: 'profile', id: userParam };
  }

  // Try pathname for session - supports both:
  // - Short share IDs (4-12 chars): /abc123
  // - Full UUIDs (36 chars with hyphens): /49859021-a140-4e2d-bc26-cec4d04768ad
  const pathMatch = pathname.match(/^\/([a-zA-Z0-9-]{4,40})$/);
  if (pathMatch) {
    return { type: 'session', id: pathMatch[1] };
  }

  // Try query param for session (e.g., /?id=abc123 or /?id=uuid-string)
  const idParam = searchParams.get('id');
  if (idParam && /^[a-zA-Z0-9-]{4,40}$/.test(idParam)) {
    return { type: 'session', id: idParam };
  }

  // Try hash path for session (e.g., /#/abc123 or /#/uuid-string)
  if (hashPath && /^[a-zA-Z0-9-]{4,40}$/.test(hashPath)) {
    return { type: 'session', id: hashPath };
  }

  return { type: 'landing' };
}

/**
 * Render share buttons section
 */
function renderShareButtons(container: HTMLElement, session: SharedSession): void {
  const shareUrl = window.location.href;
  const totalBits = session.statistics.totalBits || 0;
  const shareText = `Check out this TRNG session "${session.title}": Z-Score ${session.statistics.zScore.toFixed(2)} | ${totalBits.toLocaleString()} bits`;

  container.innerHTML = `
    <div class="share-section">
      <h3 class="share-title">Share This Session</h3>
      <div class="share-buttons">
        <button class="share-btn share-copy" title="Copy Link">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
          <span>Copy Link</span>
        </button>
        <a class="share-btn share-whatsapp" href="https://wa.me/?text=${encodeURIComponent(shareText + '\n' + shareUrl)}" target="_blank" rel="noopener noreferrer" title="Share on WhatsApp">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span>WhatsApp</span>
        </a>
        <a class="share-btn share-telegram" href="https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}" target="_blank" rel="noopener noreferrer" title="Share on Telegram">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
          <span>Telegram</span>
        </a>
        <a class="share-btn share-facebook" href="https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}" target="_blank" rel="noopener noreferrer" title="Share on Facebook">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
          </svg>
          <span>Facebook</span>
        </a>
        <a class="share-btn share-twitter" href="https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}" target="_blank" rel="noopener noreferrer" title="Share on X">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
          <span>X</span>
        </a>
      </div>
      <div class="share-url">
        <input type="text" class="share-url-input" value="${escapeHtml(shareUrl)}" readonly />
      </div>
    </div>
  `;

  // Add copy link functionality
  const copyBtn = container.querySelector('.share-copy');
  const urlInput = container.querySelector('.share-url-input') as HTMLInputElement;

  copyBtn?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      const btnText = copyBtn.querySelector('span');
      if (btnText) {
        const originalText = btnText.textContent;
        btnText.textContent = 'Copied!';
        copyBtn.classList.add('copied');
        setTimeout(() => {
          btnText.textContent = originalText;
          copyBtn.classList.remove('copied');
        }, 2000);
      }
    } catch (err) {
      // Fallback: select the input
      urlInput?.select();
      document.execCommand('copy');
    }
  });

  // Select all on input click
  urlInput?.addEventListener('click', () => {
    urlInput.select();
  });
}

/**
 * Render the full session preview
 */
function renderSession(container: HTMLElement, session: SharedSession): void {
  container.innerHTML = `
    <div class="session-preview">
      <div id="header"></div>
      <div id="graph"></div>
      <div id="player"></div>
      <div id="stats"></div>
      <div id="share"></div>
      <div id="cta"></div>
      <div id="footer"></div>
    </div>
  `;

  // Render each section
  const headerEl = document.getElementById('header');
  const graphEl = document.getElementById('graph');
  const playerEl = document.getElementById('player');
  const statsEl = document.getElementById('stats');
  const shareEl = document.getElementById('share');
  const ctaEl = document.getElementById('cta');
  const footerEl = document.getElementById('footer');

  if (headerEl) {
    renderSessionHeader({
      title: session.title,
      duration: session.duration,
      location: session.location,
      createdAt: session.createdAt,
      viewCount: session.viewCount,
    }, headerEl);
  }

  if (graphEl) {
    const totalBits = session.statistics.totalBits || session.graphData?.values?.length || 1000;
    renderDeviationGraph(session.graphData, graphEl, totalBits);
  }

  // Always render audio teaser section to encourage app downloads
  if (playerEl) {
    renderWaveformPlayer(session.audioPreview, playerEl);
  }

  if (statsEl) {
    renderStatistics(session.statistics, statsEl);
  }

  if (shareEl) {
    renderShareButtons(shareEl, session);
  }

  if (ctaEl) {
    renderDownloadCTA(ctaEl);
  }

  if (footerEl) {
    renderFooter(footerEl);
  }
}

/**
 * Initialize landing page with discovery sections
 */
async function initLandingPage(container: HTMLElement): Promise<void> {
  // Generate particles HTML
  const particlesHtml = Array.from({ length: 30 }, () => {
    const left = Math.random() * 100;
    const delay = Math.random() * 15;
    const size = 2 + Math.random() * 4;
    return `<div class="particle" style="left: ${left}%; animation-delay: ${delay}s; width: ${size}px; height: ${size}px;"></div>`;
  }).join('');

  container.innerHTML = `
    <div class="particles">${particlesHtml}</div>
    <div class="landing-container">
      <!-- Hero -->
      <div class="hero">
        <h1 class="hero-title">Ecstatic Oracle</h1>
        <p class="hero-tagline">A Dance That Listens Back</p>
        <p class="hero-subtitle">Consciousness meets randomness</p>
        <nav class="hero-nav">
          <a href="/discover" class="hero-nav-link">
            <span class="hero-nav-icon">🔮</span>
            <span>Discover</span>
          </a>
          <a href="/events" class="hero-nav-link">
            <span class="hero-nav-icon">📅</span>
            <span>Events</span>
          </a>
          <a href="#community" class="hero-nav-link">
            <span class="hero-nav-icon">👥</span>
            <span>Community</span>
          </a>
          <a href="#explore" class="hero-nav-link">
            <span class="hero-nav-icon">🌍</span>
            <span>Explore</span>
          </a>
        </nav>
      </div>

      <!-- Recent Sessions -->
      <section class="section">
        <div class="section-header">
          <span class="section-title">Recent Sessions</span>
          <div class="section-line"></div>
          <a href="/discover" class="section-link">Browse All →</a>
        </div>
        <div id="sessions-list" class="discovery-grid">
          <div class="empty-state">Loading sessions...</div>
        </div>
      </section>

      <!-- Upcoming Events -->
      <section class="section">
        <div class="section-header">
          <span class="section-title">Upcoming Events</span>
          <div class="section-line"></div>
          <a href="/events" class="section-link">Browse All →</a>
        </div>
        <div id="events-list" class="discovery-grid">
          <div class="empty-state">Loading events...</div>
        </div>
      </section>

      <!-- Community -->
      <section id="community" class="section">
        <div class="section-header">
          <span class="section-title">Community</span>
          <div class="section-line"></div>
        </div>
        <div id="profiles-list" class="profiles-grid">
          <div class="empty-state">Loading profiles...</div>
        </div>
      </section>

      <!-- Search -->
      <section id="explore" class="section">
        <div class="section-header">
          <span class="section-title">Explore</span>
          <div class="section-line"></div>
        </div>
        <div class="search-section">
          <div>
            <label class="search-label">View Session by Share Code</label>
            <form id="share-form" class="search-form">
              <input type="text" id="share-input" class="search-input" placeholder="Enter share code" pattern="[a-zA-Z0-9]{4,12}" />
              <button type="submit" class="search-button">View</button>
            </form>
          </div>
          <div>
            <label class="search-label">View User Profile</label>
            <form id="profile-form" class="search-form">
              <input type="text" id="profile-input" class="search-input" placeholder="Enter user ID" />
              <button type="submit" class="search-button">View</button>
            </form>
          </div>
        </div>
      </section>

      <div id="cta"></div>
      <div id="footer"></div>
    </div>
  `;

  // Add session form handler
  const form = document.getElementById('share-form');
  const input = document.getElementById('share-input') as HTMLInputElement;

  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    const code = input.value.trim();
    if (code && /^[a-zA-Z0-9]{4,12}$/.test(code)) {
      window.location.href = `/${code}`;
    }
  });

  // Add profile form handler
  const profileForm = document.getElementById('profile-form');
  const profileInput = document.getElementById('profile-input') as HTMLInputElement;

  profileForm?.addEventListener('submit', (e) => {
    e.preventDefault();
    const userId = profileInput.value.trim();
    if (userId && userId.length >= 20) {
      window.location.href = `/user/${userId}`;
    }
  });

  // Render CTA and footer
  const ctaEl = document.getElementById('cta');
  const footerEl = document.getElementById('footer');

  if (ctaEl) renderDownloadCTA(ctaEl);
  if (footerEl) renderFooter(footerEl);

  // Load discovery data
  await loadDiscoveryData();
}

/**
 * Initialize the dedicated discover page
 * More focused on browsing sessions with filters
 */
async function initDiscoverPage(
  container: HTMLElement,
  filters?: { category?: string; location?: string }
): Promise<void> {
  // Update page title
  document.title = 'Discover - Ecstatic Oracle';

  // Generate particles HTML
  const particlesHtml = Array.from({ length: 20 }, () => {
    const left = Math.random() * 100;
    const delay = Math.random() * 15;
    const size = 2 + Math.random() * 4;
    return `<div class="particle" style="left: ${left}%; animation-delay: ${delay}s; width: ${size}px; height: ${size}px;"></div>`;
  }).join('');

  // Categories for filtering
  const categories = [
    { id: '', name: 'All', emoji: '✨' },
    { id: 'ecstatic_dance', name: 'Ecstatic Dance', emoji: '💃' },
    { id: 'meditation', name: 'Meditation', emoji: '🧘' },
    { id: 'ceremony', name: 'Ceremony', emoji: '🔥' },
    { id: 'sound_healing', name: 'Sound Healing', emoji: '🎵' },
    { id: 'festival', name: 'Festival', emoji: '🎪' },
    { id: 'other', name: 'Other', emoji: '🌟' },
  ];

  const activeCategory = filters?.category || '';

  container.innerHTML = `
    <div class="particles">${particlesHtml}</div>
    <div class="discover-container">
      <!-- Header -->
      <header class="discover-header">
        <a href="/" class="discover-back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </a>
        <div>
          <h1 class="discover-title">Discover</h1>
          <p class="discover-subtitle">Explore sessions from the community</p>
        </div>
        <a href="/discover/map" class="discover-map-link" title="View on map">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          <span>Map</span>
        </a>
      </header>

      <!-- Search Bar -->
      <div class="discover-search">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/>
            <path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            id="session-search"
            class="search-input"
            placeholder="Search sessions by title, location, or creator..."
          />
          <button id="search-clear" class="search-clear" style="display: none;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Category Filter -->
      <div class="discover-filters">
        <div class="category-tabs" id="category-tabs">
          ${categories.map(cat => `
            <button
              class="category-tab ${activeCategory === cat.id ? 'active' : ''}"
              data-category="${cat.id}"
            >
              <span class="category-emoji">${cat.emoji}</span>
              <span class="category-name">${cat.name}</span>
            </button>
          `).join('')}
        </div>
      </div>

      <!-- Sessions Grid -->
      <section class="discover-section">
        <div class="section-header">
          <span class="section-title">Recent Sessions</span>
          <span class="section-count" id="session-count"></span>
        </div>
        <div id="sessions-grid" class="discover-sessions-grid">
          <div class="loading-state">
            <div class="loading-spinner"></div>
            <span>Loading sessions...</span>
          </div>
        </div>
        <div id="load-more-container" class="load-more-container" style="display: none;">
          <button id="load-more-btn" class="load-more-btn">Load More</button>
        </div>
      </section>

      <!-- Featured DJs -->
      <section class="discover-section">
        <div class="section-header">
          <span class="section-title">Featured DJs</span>
          <div class="section-line"></div>
        </div>
        <div id="featured-djs" class="featured-djs-grid">
          <div class="loading-state">
            <div class="loading-spinner small"></div>
          </div>
        </div>
      </section>

      <!-- Community Section -->
      <section class="discover-section">
        <div class="section-header">
          <span class="section-title">Active Explorers</span>
          <div class="section-line"></div>
        </div>
        <div id="explorers-grid" class="explorers-grid">
          <div class="loading-state">
            <div class="loading-spinner small"></div>
          </div>
        </div>
      </section>

      <div id="cta"></div>
      <div id="footer"></div>
    </div>
  `;

  // Setup category filter handlers
  const categoryTabs = document.getElementById('category-tabs');
  categoryTabs?.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const button = target.closest('.category-tab') as HTMLButtonElement;
    if (button) {
      const category = button.dataset.category || '';
      const url = category ? `/discover?category=${category}` : '/discover';
      window.history.pushState({}, '', url);

      // Update active state
      document.querySelectorAll('.category-tab').forEach(tab => tab.classList.remove('active'));
      button.classList.add('active');

      // Reload sessions with filter
      loadDiscoverSessions(category);
    }
  });

  // Set up search functionality
  setupSearchInput();

  // Render CTA and footer
  const ctaEl = document.getElementById('cta');
  const footerEl = document.getElementById('footer');
  if (ctaEl) renderDownloadCTA(ctaEl);
  if (footerEl) renderFooter(footerEl);

  // Set up infinite scroll
  setupInfiniteScroll();

  // Load initial data
  await Promise.all([
    loadDiscoverSessions(activeCategory),
    loadFeaturedDJs(),
    loadActiveExplorers(),
  ]);
}

/**
 * Load sessions for discover page with infinite scroll support
 */
async function loadDiscoverSessions(category?: string, reset: boolean = true): Promise<void> {
  const grid = document.getElementById('sessions-grid');
  const countEl = document.getElementById('session-count');

  if (!grid) return;

  // Reset pagination state if this is a new load
  if (reset) {
    discoverPaginationState = {
      sessions: [],
      lastCursor: null,
      hasMore: true,
      isLoading: false,
      category: category || null,
      searchQuery: '',
    };

    grid.innerHTML = `
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <span>Loading sessions...</span>
      </div>
    `;
  }

  // Prevent concurrent loads
  if (discoverPaginationState.isLoading) return;
  discoverPaginationState.isLoading = true;

  try {
    const result = await listPublicSessions(20, discoverPaginationState.lastCursor || undefined);

    // Update pagination state
    discoverPaginationState.sessions = [...discoverPaginationState.sessions, ...result.sessions];
    discoverPaginationState.lastCursor = result.lastCursor;
    discoverPaginationState.hasMore = result.hasMore;
    discoverPaginationState.isLoading = false;

    if (discoverPaginationState.sessions.length === 0) {
      grid.innerHTML = `
        <div class="empty-state">
          <span class="empty-icon">🔍</span>
          <p>No sessions found</p>
          <p class="empty-hint">Be the first to share a session!</p>
        </div>
      `;
      if (countEl) countEl.textContent = '0 sessions';
      return;
    }

    // Filter by category client-side for now
    let sessions = discoverPaginationState.sessions;
    if (category) {
      sessions = sessions.filter(s => s.category === category || s.categoryName?.toLowerCase().includes(category.replace('_', ' ')));
    }

    // Filter by search query if present
    if (discoverPaginationState.searchQuery) {
      const query = discoverPaginationState.searchQuery.toLowerCase();
      sessions = sessions.filter(s =>
        s.title.toLowerCase().includes(query) ||
        s.location?.toLowerCase().includes(query) ||
        s.userDisplayName.toLowerCase().includes(query) ||
        s.categoryName?.toLowerCase().includes(query)
      );
    }

    if (countEl) countEl.textContent = `${sessions.length} sessions`;

    grid.innerHTML = sessions.map(session => renderSessionCard(session)).join('');

    // Add loading indicator at the bottom if more sessions available
    if (discoverPaginationState.hasMore) {
      grid.innerHTML += `
        <div id="load-more-trigger" class="load-more-trigger">
          <div class="loading-spinner small"></div>
          <span>Loading more...</span>
        </div>
      `;
    }

  } catch (error) {
    console.error('Failed to load sessions:', error);
    discoverPaginationState.isLoading = false;
    grid.innerHTML = `
      <div class="error-state">
        <span class="error-icon">⚠️</span>
        <p>Failed to load sessions</p>
        <button onclick="window.location.reload()" class="retry-btn">Retry</button>
      </div>
    `;
  }
}

/**
 * Render a single session card
 */
function renderSessionCard(session: DiscoverySession): string {
  const stats = session.statistics;
  const categoryEmoji = session.categoryEmoji || '✨';

  return `
    <a href="/${escapeHtml(session.shareId)}" class="discover-session-card">
      <div class="session-card-header">
        <span class="session-category-emoji">${categoryEmoji}</span>
        <span class="session-duration">${formatDuration(session.duration)}</span>
      </div>
      <h3 class="session-card-title">${escapeHtml(session.title)}</h3>
      <div class="session-card-meta">
        <span class="session-author">by ${escapeHtml(session.userDisplayName)}</span>
        ${session.location ? `<span class="session-location">📍 ${escapeHtml(session.location)}</span>` : ''}
      </div>
      <div class="session-card-stats">
        ${stats ? `
          <span class="stat-badge zscore">Z: ${stats.zScore.toFixed(2)}</span>
          <span class="stat-badge bits">${formatBits(stats.totalBits)} bits</span>
        ` : ''}
        ${session.hasAudio ? '<span class="stat-badge audio">🎵</span>' : ''}
      </div>
    </a>
  `;
}

/**
 * Set up infinite scroll for discover page
 */
function setupInfiniteScroll(): void {
  let scrollTimeout: number | null = null;

  window.addEventListener('scroll', () => {
    if (scrollTimeout) return;

    scrollTimeout = window.setTimeout(() => {
      scrollTimeout = null;

      // Check if we're near the bottom of the page
      const scrollPosition = window.innerHeight + window.scrollY;
      const pageHeight = document.documentElement.scrollHeight;
      const triggerPoint = pageHeight - 500; // Load more when 500px from bottom

      if (scrollPosition >= triggerPoint &&
          discoverPaginationState.hasMore &&
          !discoverPaginationState.isLoading) {
        loadDiscoverSessions(discoverPaginationState.category || undefined, false);
      }
    }, 100);
  });
}

/**
 * Set up search input with debounced filtering
 */
function setupSearchInput(): void {
  const searchInput = document.getElementById('session-search') as HTMLInputElement;
  const clearBtn = document.getElementById('search-clear');
  let debounceTimeout: number | null = null;

  if (!searchInput) return;

  searchInput.addEventListener('input', () => {
    const query = searchInput.value.trim();

    // Show/hide clear button
    if (clearBtn) {
      clearBtn.style.display = query ? 'flex' : 'none';
    }

    // Debounce search
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    debounceTimeout = window.setTimeout(() => {
      discoverPaginationState.searchQuery = query;
      // Re-render filtered sessions
      renderFilteredSessions();
    }, 300);
  });

  // Clear button handler
  clearBtn?.addEventListener('click', () => {
    searchInput.value = '';
    if (clearBtn) clearBtn.style.display = 'none';
    discoverPaginationState.searchQuery = '';
    renderFilteredSessions();
  });
}

/**
 * Re-render sessions with current filters and search
 */
function renderFilteredSessions(): void {
  const grid = document.getElementById('sessions-grid');
  const countEl = document.getElementById('session-count');
  if (!grid) return;

  let sessions = discoverPaginationState.sessions;

  // Filter by category
  if (discoverPaginationState.category) {
    const category = discoverPaginationState.category;
    sessions = sessions.filter(s =>
      s.category === category ||
      s.categoryName?.toLowerCase().includes(category.replace('_', ' '))
    );
  }

  // Filter by search query
  if (discoverPaginationState.searchQuery) {
    const query = discoverPaginationState.searchQuery.toLowerCase();
    sessions = sessions.filter(s =>
      s.title.toLowerCase().includes(query) ||
      s.location?.toLowerCase().includes(query) ||
      s.userDisplayName.toLowerCase().includes(query) ||
      s.categoryName?.toLowerCase().includes(query)
    );
  }

  if (countEl) countEl.textContent = `${sessions.length} sessions`;

  if (sessions.length === 0) {
    grid.innerHTML = `
      <div class="empty-state">
        <span class="empty-icon">🔍</span>
        <p>No sessions found</p>
        <p class="empty-hint">${discoverPaginationState.searchQuery ? 'Try a different search term' : 'Be the first to share a session!'}</p>
      </div>
    `;
    return;
  }

  grid.innerHTML = sessions.map(session => renderSessionCard(session)).join('');

  // Add loading indicator if more sessions available
  if (discoverPaginationState.hasMore && !discoverPaginationState.searchQuery) {
    grid.innerHTML += `
      <div id="load-more-trigger" class="load-more-trigger">
        <div class="loading-spinner small"></div>
        <span>Loading more...</span>
      </div>
    `;
  }
}

/**
 * Initialize map page
 */
async function initMapPage(container: HTMLElement): Promise<void> {
  // Update page title
  document.title = 'Session Map - Ecstatic Oracle';

  // Generate particles
  const particlesHtml = Array.from({ length: 15 }, () => {
    const left = Math.random() * 100;
    const delay = Math.random() * 15;
    const size = 2 + Math.random() * 4;
    return `<div class="particle" style="left: ${left}%; animation-delay: ${delay}s; width: ${size}px; height: ${size}px;"></div>`;
  }).join('');

  container.innerHTML = `
    <div class="particles">${particlesHtml}</div>
    <div class="map-page-container">
      <header class="map-header">
        <a href="/discover" class="discover-back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to List
        </a>
        <h1 class="map-title">Session Map</h1>
      </header>
      <div id="map-container" class="map-container">
        <div class="loading-state">
          <div class="loading-spinner"></div>
          <span>Loading map...</span>
        </div>
      </div>
      <div id="map-info" class="map-info"></div>
    </div>
  `;

  // Load all sessions with location data
  try {
    // Load more sessions for the map (up to 100)
    let allSessions: DiscoverySession[] = [];
    let cursor: string | null = null;
    let hasMore = true;

    while (hasMore && allSessions.length < 100) {
      const result = await listPublicSessions(50, cursor || undefined);
      allSessions = [...allSessions, ...result.sessions];
      cursor = result.lastCursor;
      hasMore = result.hasMore;
    }

    // Filter to only sessions with location data
    const sessionsWithLocation = allSessions.filter(s => s.locationData?.latitude && s.locationData?.longitude);

    if (sessionsWithLocation.length === 0) {
      const mapContainer = document.getElementById('map-container');
      if (mapContainer) {
        mapContainer.innerHTML = `
          <div class="empty-state">
            <span class="empty-icon">📍</span>
            <p>No sessions with location data</p>
            <p class="empty-hint">Sessions will appear here once they have GPS coordinates</p>
            <a href="/discover" class="btn-primary" style="margin-top: 16px;">Back to List</a>
          </div>
        `;
      }
      return;
    }

    // Initialize Leaflet map
    initLeafletMap(sessionsWithLocation);

    const mapInfo = document.getElementById('map-info');
    if (mapInfo) {
      mapInfo.innerHTML = `<span>${sessionsWithLocation.length} sessions with location data</span>`;
    }
  } catch (error) {
    console.error('Failed to load map data:', error);
    const mapContainer = document.getElementById('map-container');
    if (mapContainer) {
      mapContainer.innerHTML = `
        <div class="error-state">
          <span class="error-icon">⚠️</span>
          <p>Failed to load map data</p>
          <button onclick="window.location.reload()" class="retry-btn">Retry</button>
        </div>
      `;
    }
  }
}

/**
 * Initialize Leaflet map with sessions
 */
function initLeafletMap(sessions: DiscoverySession[]): void {
  const mapContainer = document.getElementById('map-container');
  if (!mapContainer) return;

  // Clear loading state
  mapContainer.innerHTML = '<div id="leaflet-map" style="width: 100%; height: 100%;"></div>';

  // Check if Leaflet is available
  if (typeof (window as any).L === 'undefined') {
    mapContainer.innerHTML = `
      <div class="error-state">
        <span class="error-icon">⚠️</span>
        <p>Map library not loaded</p>
        <p class="empty-hint">Please refresh the page</p>
      </div>
    `;
    return;
  }

  const L = (window as any).L;

  // Calculate center from sessions
  const lats = sessions.map(s => s.locationData!.latitude);
  const lngs = sessions.map(s => s.locationData!.longitude);
  const centerLat = lats.reduce((a, b) => a + b, 0) / lats.length;
  const centerLng = lngs.reduce((a, b) => a + b, 0) / lngs.length;

  // Create map
  const map = L.map('leaflet-map').setView([centerLat, centerLng], 4);

  // Add dark tile layer (CartoDB Dark Matter)
  L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    subdomains: 'abcd',
    maxZoom: 19
  }).addTo(map);

  // Custom marker icon
  const markerIcon = L.divIcon({
    className: 'custom-marker',
    html: '<div class="marker-dot"></div>',
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });

  // Add markers for each session
  const markers = sessions.map(session => {
    const marker = L.marker(
      [session.locationData!.latitude, session.locationData!.longitude],
      { icon: markerIcon }
    );

    // Create popup content
    const popupContent = `
      <div class="map-popup">
        <div class="popup-emoji">${session.categoryEmoji || '✨'}</div>
        <h3 class="popup-title">${escapeHtml(session.title)}</h3>
        <p class="popup-location">${escapeHtml(session.location || session.locationData?.locality || 'Unknown location')}</p>
        <p class="popup-author">by ${escapeHtml(session.userDisplayName)}</p>
        <a href="/${escapeHtml(session.shareId)}" class="popup-link">View Session →</a>
      </div>
    `;

    marker.bindPopup(popupContent, {
      maxWidth: 250,
      className: 'custom-popup'
    });

    marker.addTo(map);
    return marker;
  });

  // Fit map to show all markers
  if (markers.length > 1) {
    const group = L.featureGroup(markers);
    map.fitBounds(group.getBounds(), { padding: [50, 50] });
  }
}

/**
 * Load featured DJs
 */
async function loadFeaturedDJs(): Promise<void> {
  const grid = document.getElementById('featured-djs');
  if (!grid) return;

  try {
    const result = await listPublicProfiles(10);
    const djs = result.profiles.filter(p => p.isDJ);

    if (djs.length === 0) {
      grid.innerHTML = `
        <div class="empty-state small">
          <p>No DJs yet</p>
        </div>
      `;
      return;
    }

    grid.innerHTML = djs.slice(0, 6).map(dj => {
      const initials = dj.displayName
        ? dj.displayName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
        : '?';

      return `
        <a href="/user/${escapeHtml(dj.userId)}" class="featured-dj-card">
          ${dj.photoURL
            ? `<img src="${escapeHtml(dj.photoURL)}" alt="" class="dj-avatar" />`
            : `<div class="dj-avatar-initials">${escapeHtml(initials)}</div>`
          }
          <div class="dj-info">
            <span class="dj-name">${escapeHtml(dj.djProfile?.stageName || dj.displayName)}</span>
            <span class="dj-sessions">${dj.sessionCount} sessions</span>
          </div>
          <span class="dj-badge">DJ</span>
        </a>
      `;
    }).join('');

  } catch (error) {
    console.error('Failed to load DJs:', error);
    grid.innerHTML = '<div class="error-state small"><p>Failed to load</p></div>';
  }
}

/**
 * Load active explorers (non-DJ users)
 */
async function loadActiveExplorers(): Promise<void> {
  const grid = document.getElementById('explorers-grid');
  if (!grid) return;

  try {
    const result = await listPublicProfiles(20);
    const explorers = result.profiles.filter(p => !p.isDJ && p.sessionCount > 0);

    if (explorers.length === 0) {
      grid.innerHTML = `
        <div class="empty-state small">
          <p>No explorers yet</p>
        </div>
      `;
      return;
    }

    grid.innerHTML = explorers.slice(0, 8).map(explorer => {
      const initials = explorer.displayName
        ? explorer.displayName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
        : '?';
      const locationText = explorer.location
        ? [explorer.location.locality, explorer.location.country].filter(Boolean).join(', ')
        : null;

      return `
        <a href="/user/${escapeHtml(explorer.userId)}" class="explorer-card">
          ${explorer.photoURL
            ? `<img src="${escapeHtml(explorer.photoURL)}" alt="" class="explorer-avatar" />`
            : `<div class="explorer-avatar-initials">${escapeHtml(initials)}</div>`
          }
          <span class="explorer-name">${escapeHtml(explorer.displayName)}</span>
          ${locationText ? `<span class="explorer-location">📍 ${escapeHtml(locationText)}</span>` : ''}
          <span class="explorer-stats">${explorer.sessionCount} sessions</span>
        </a>
      `;
    }).join('');

  } catch (error) {
    console.error('Failed to load explorers:', error);
    grid.innerHTML = '<div class="error-state small"><p>Failed to load</p></div>';
  }
}

/**
 * Load and render discovery data (profiles and sessions)
 */
async function loadDiscoveryData(): Promise<void> {
  const sessionsEl = document.getElementById('sessions-list');
  const profilesEl = document.getElementById('profiles-list');
  const eventsEl = document.getElementById('events-list');

  // Load sessions, profiles, and events in parallel
  const [sessionsResult, profilesResult, eventsResult] = await Promise.allSettled([
    listPublicSessions(10),
    listPublicProfiles(10),
    listPublicEvents(6),
  ]);

  // Render sessions
  if (sessionsEl) {
    if (sessionsResult.status === 'fulfilled' && sessionsResult.value.sessions.length > 0) {
      renderDiscoverySessions(sessionsResult.value.sessions, sessionsEl);
    } else {
      sessionsEl.innerHTML = `
        <div style="text-align: center; padding: 20px; color: var(--color-text-secondary);">
          ${sessionsResult.status === 'rejected' ? 'Failed to load sessions' : 'No public sessions yet'}
        </div>
      `;
    }
  }

  // Render events
  if (eventsEl) {
    if (eventsResult.status === 'fulfilled' && eventsResult.value.events.length > 0) {
      renderDiscoveryEvents(eventsResult.value.events, eventsEl);
    } else {
      eventsEl.innerHTML = `
        <div style="text-align: center; padding: 20px; color: var(--color-text-secondary);">
          ${eventsResult.status === 'rejected' ? 'Failed to load events' : 'No events yet'}
        </div>
      `;
    }
  }

  // Render profiles
  if (profilesEl) {
    if (profilesResult.status === 'fulfilled' && profilesResult.value.profiles.length > 0) {
      renderDiscoveryProfiles(profilesResult.value.profiles, profilesEl);
    } else {
      profilesEl.innerHTML = `
        <div style="text-align: center; padding: 20px; color: var(--color-text-secondary);">
          ${profilesResult.status === 'rejected' ? 'Failed to load profiles' : 'No public profiles yet'}
        </div>
      `;
    }
  }
}

/**
 * Render discovery sessions list
 */
function renderDiscoverySessions(sessions: DiscoverySession[], container: HTMLElement): void {
  container.innerHTML = sessions.map(session => {
    const stats = session.statistics;

    return `
      <a href="/${escapeHtml(session.shareId)}" class="discovery-session">
        <div class="discovery-session-header">
          <span class="discovery-session-title">${escapeHtml(session.title)}</span>
          <span class="discovery-session-duration">${formatDuration(session.duration)}</span>
        </div>
        <div class="discovery-session-meta">
          <span class="discovery-session-author">
            by ${escapeHtml(session.userDisplayName)}
            ${session.location ? ` · ${escapeHtml(session.location)}` : ''}
          </span>
          <div class="discovery-session-badges">
            ${session.hasAudio ? `
              <span class="badge-audio" title="Audio available in the app">
                🎵 Audio
              </span>
            ` : ''}
            ${stats ? `
              <span class="badge-zscore">
                Z: ${stats.zScore.toFixed(2)}
              </span>
            ` : ''}
          </div>
        </div>
      </a>
    `;
  }).join('');
}

/**
 * Render discovery events list (landing page)
 */
function renderDiscoveryEvents(events: import('./api/firebase').PublicEvent[], container: HTMLElement): void {
  container.innerHTML = events.map(event => {
    const statusLabel = event.status === 'lobby' ? 'Upcoming'
      : event.status === 'active' ? 'Live Now'
      : 'Ended';
    const statusClass = event.status;
    const locationText = event.location?.locality
      ? (event.location.country ? `${event.location.locality}, ${event.location.country}` : event.location.locality)
      : null;
    const timeText = event.scheduledStartTime
      ? new Date(event.scheduledStartTime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : null;

    return `
      <a href="/event/${escapeHtml(event.joinCode)}" class="discovery-event">
        <div class="discovery-event-header">
          <span class="discovery-event-title">${escapeHtml(event.title)}</span>
          <span class="discovery-event-status ${statusClass}">${statusLabel}</span>
        </div>
        <div class="discovery-event-meta">
          <span class="discovery-event-organizer">
            by ${escapeHtml(event.organizerName)}
            ${locationText ? ` · ${escapeHtml(locationText)}` : ''}
          </span>
          <div class="discovery-event-info">
            ${timeText ? `<span class="discovery-event-time">${timeText}</span>` : ''}
            <span class="discovery-event-participants">${event.participantCount}/${event.maxDevices} joined</span>
          </div>
        </div>
      </a>
    `;
  }).join('');
}

/**
 * Render discovery profiles list
 */
function renderDiscoveryProfiles(profiles: DiscoveryProfile[], container: HTMLElement): void {
  container.innerHTML = profiles.map(profile => {
    const initials = profile.displayName
      ? profile.displayName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
      : '?';
    const locationText = profile.location
      ? [profile.location.locality, profile.location.country].filter(Boolean).join(', ')
      : null;

    return `
      <a href="/user/${escapeHtml(profile.userId)}" class="discovery-profile">
        <div class="discovery-profile-header">
          ${profile.photoURL
            ? `<img src="${escapeHtml(profile.photoURL)}" alt="" class="discovery-profile-avatar" />`
            : `<div class="discovery-profile-avatar-initials">${escapeHtml(initials)}</div>`
          }
          <div>
            <div class="discovery-profile-name">${escapeHtml(profile.displayName)}</div>
            ${profile.isDJ && profile.djProfile?.stageName
              ? `<div class="discovery-profile-stage">"${escapeHtml(profile.djProfile.stageName)}"</div>`
              : ''
            }
          </div>
        </div>
        ${locationText
          ? `<div class="discovery-profile-location">📍 ${escapeHtml(locationText)}</div>`
          : ''
        }
        <div class="discovery-profile-stats">
          ${profile.sessionCount} sessions · ${formatBits(profile.totalBitsGenerated)} bits
        </div>
        ${profile.isDJ
          ? `<span class="discovery-profile-badge">DJ</span>`
          : ''
        }
      </a>
    `;
  }).join('');
}

/**
 * Format large bit counts
 */
function formatBits(bits: number): string {
  if (bits >= 1000000000) {
    return `${(bits / 1000000000).toFixed(1)}B`;
  }
  if (bits >= 1000000) {
    return `${(bits / 1000000).toFixed(1)}M`;
  }
  if (bits >= 1000) {
    return `${(bits / 1000).toFixed(1)}K`;
  }
  return bits.toString();
}

/**
 * Render error page
 */
function renderError(container: HTMLElement, message: string, title = 'Not Found'): void {
  container.innerHTML = `
    <div class="error-container">
      <div class="error-icon">:(</div>
      <h2>${escapeHtml(title)}</h2>
      <p class="error-message">${escapeHtml(message)}</p>
      <a
        href="/"
        style="
          display: inline-block;
          margin-top: 20px;
          padding: 12px 24px;
          background: var(--color-accent);
          border-radius: 8px;
          color: white;
          text-decoration: none;
          font-weight: 600;
        "
      >
        Go Home
      </a>
    </div>
  `;
}

/**
 * Render the user profile page
 */
function renderProfile(container: HTMLElement, data: ProfileResponse): void {
  container.innerHTML = `
    <div class="profile-preview">
      <div id="profile-header"></div>
      <div id="profile-stats"></div>
      <div id="profile-dj"></div>
      <div id="profile-sessions"></div>
      <div id="cta"></div>
      <div id="footer"></div>
    </div>
  `;

  // Render each section
  const headerEl = document.getElementById('profile-header');
  const statsEl = document.getElementById('profile-stats');
  const djEl = document.getElementById('profile-dj');
  const sessionsEl = document.getElementById('profile-sessions');
  const ctaEl = document.getElementById('cta');
  const footerEl = document.getElementById('footer');

  if (headerEl) {
    renderProfileHeader(data.profile, headerEl);
  }

  if (statsEl) {
    renderProfileStats(data.profile, statsEl);
  }

  if (djEl) {
    renderDJProfile(data.profile, djEl);
  }

  if (sessionsEl) {
    renderProfileSessions(data.sessions, sessionsEl);
  }

  if (ctaEl) {
    renderDownloadCTA(ctaEl);
  }

  if (footerEl) {
    renderFooter(footerEl);
  }
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Initialize the app
init();
